/**
 * WVB-03 — Approval Policy Administration Page
 * Route: /settings/approval-policies
 *
 * Provides governed creation, editing, and deactivation of
 * event budget approval policies (budget.policy.manage required).
 * Viewers with budget.view see read-only list.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Shield, Plus, ChevronRight, X, CheckCircle2,
  Loader2, AlertCircle, Edit2, PowerOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { approvalApi, hasCapability } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_BADGE = {
  ACTIVE:   "bg-green-100 text-green-700 border border-green-200",
  INACTIVE: "bg-slate-100 text-slate-500 border border-slate-200",
};

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "SGD", "AED", "HKD"];

function fmt(minor, currency) {
  if (minor === null || minor === undefined) return "No limit";
  const divisor = currency === "JPY" ? 1 : 100;
  const val = minor / divisor;
  return `${currency} ${val.toLocaleString("en-IN", { minimumFractionDigits: 0 })}`;
}

const BLANK_FORM = {
  name: "",
  description: "",
  policy_type: "EVENT_COST_BUDGET",
  currency_code: "INR",
  min_amount_minor: 0,
  max_amount_minor: "",
  required_role: "FINANCE_APPROVER",
  required_capability: "budget.approve",
  auto_approve: false,
  effective_from: "",
  effective_until: "",
  notes: "",
};

function PolicyForm({ initial = BLANK_FORM, onSave, onCancel, mode = "create" }) {
  const [form, setForm] = useState({ ...BLANK_FORM, ...initial });
  const [saving, setSaving] = useState(false);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave(e) {
    e.preventDefault();
    if (!form.name.trim() || form.name.length < 3) {
      toast.error("Policy name must be at least 3 characters");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      min_amount_minor: parseInt(form.min_amount_minor || 0, 10),
      max_amount_minor: form.max_amount_minor ? parseInt(form.max_amount_minor, 10) : null,
      effective_from: form.effective_from || null,
      effective_until: form.effective_until || null,
    };
    try {
      await onSave(payload);
    } finally { setSaving(false); }
  }

  return (
    <form onSubmit={handleSave} className="space-y-4" data-testid="policy-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1 block">Policy Name *</label>
          <input type="text" value={form.name} onChange={e => set("name", e.target.value)}
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            placeholder="Default INR Approval Policy" data-testid="policy-name-input" required />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1 block">Currency *</label>
          <select value={form.currency_code} onChange={e => set("currency_code", e.target.value)}
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            data-testid="policy-currency-select">
            {CURRENCIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1 block">Min Amount (minor units)</label>
          <input type="number" value={form.min_amount_minor} onChange={e => set("min_amount_minor", e.target.value)}
            min={0} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            placeholder="0 (any amount)" data-testid="policy-min-amount-input" />
          <p className="text-xs text-slate-400 mt-0.5">INR: 100 = ₹1. 0 = any amount</p>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1 block">Max Amount (minor units)</label>
          <input type="number" value={form.max_amount_minor} onChange={e => set("max_amount_minor", e.target.value)}
            min={0} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            placeholder="Leave blank for no upper limit" data-testid="policy-max-amount-input" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1 block">Required Approver Role *</label>
          <input type="text" value={form.required_role} onChange={e => set("required_role", e.target.value)}
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            data-testid="policy-required-role-input" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1 block">Effective From</label>
          <input type="date" value={form.effective_from || ""} onChange={e => set("effective_from", e.target.value)}
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-500 mb-1 block">Description</label>
        <textarea value={form.description || ""} onChange={e => set("description", e.target.value)}
          rows={2} placeholder="Optional policy description"
          className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400" />
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" id="auto_approve" checked={form.auto_approve}
          onChange={e => set("auto_approve", e.target.checked)} className="w-4 h-4" />
        <label htmlFor="auto_approve" className="text-sm text-slate-600">Auto-approve (skip manual review)</label>
      </div>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={saving} data-testid="save-policy-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
          {mode === "create" ? "Create Policy" : "Save Changes"}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

export default function ApprovalPolicyAdminPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null);

  const canManage = hasCapability(user, "budget.policy.manage");

  const load = useCallback(async () => {
    try {
      const res = await approvalApi.listPolicies();
      setPolicies(res.data?.policies || []);
    } catch { toast.error("Failed to load policies"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleCreate(payload) {
    try {
      await approvalApi.createPolicy(payload);
      toast.success("Policy created");
      setShowCreate(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); throw e; }
  }

  async function handleUpdate(pid, payload) {
    try {
      await approvalApi.updatePolicy(pid, payload);
      toast.success("Policy updated");
      setEditId(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); throw e; }
  }

  async function handleDeactivate(pid) {
    try {
      await approvalApi.deactivatePolicy(pid);
      toast.success("Policy deactivated");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Deactivation failed"); }
  }

  if (!hasCapability(user, "budget.view")) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-500">Access denied. budget.view required.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="approval-policy-admin-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-slate-600">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Shield className="w-5 h-5 text-indigo-500" />
          <div>
            <h1 className="text-xl font-bold text-slate-900">Approval Policies</h1>
            <p className="text-xs text-slate-400">Event budget approval governance configuration</p>
          </div>
          {canManage && !showCreate && (
            <Button size="sm" className="ml-auto" onClick={() => setShowCreate(true)} data-testid="create-policy-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New Policy
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6 space-y-5">

        {/* Create form */}
        {showCreate && canManage && (
          <div className="bg-white rounded-xl border border-indigo-200 p-5 space-y-3" data-testid="create-policy-form">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-slate-800">New Approval Policy</p>
              <button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
            <PolicyForm mode="create" onSave={handleCreate} onCancel={() => setShowCreate(false)} />
          </div>
        )}

        {/* Policy list */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
          </div>
        ) : policies.length === 0 ? (
          <div className="flex flex-col items-center py-16 gap-3 text-slate-400" data-testid="no-policies">
            <Shield className="w-10 h-10 text-slate-200" />
            <p className="text-sm">No approval policies configured.</p>
            {canManage && !showCreate && (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                <Plus className="w-4 h-4 mr-1.5" /> Create First Policy
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {policies.map(p => (
              <div key={p.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`policy-row-${p.id}`}>
                <div className="p-4 flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-slate-900">{p.name}</span>
                    <span className="text-slate-400 text-xs">{p.policy_number}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_BADGE[p.status] || STATUS_BADGE.INACTIVE}`}>
                      {p.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm flex-wrap">
                    <div>
                      <span className="text-slate-400 text-xs">Currency </span>
                      <span className="font-semibold text-slate-700">{p.currency_code}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 text-xs">Min </span>
                      <span className="text-slate-600 text-xs">{fmt(p.min_amount_minor, p.currency_code)}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 text-xs">Required role </span>
                      <span className="text-xs bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">{p.required_role}</span>
                    </div>
                    {canManage && p.status === "ACTIVE" && (
                      <div className="flex gap-1">
                        <button onClick={() => setEditId(editId === p.id ? null : p.id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                          data-testid={`edit-policy-${p.id}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDeactivate(p.id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          data-testid={`deactivate-policy-${p.id}`}>
                          <PowerOff className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {p.description && (
                  <div className="px-4 pb-3 text-xs text-slate-400">{p.description}</div>
                )}
                {editId === p.id && canManage && (
                  <div className="px-4 pb-4 pt-2 border-t border-slate-100">
                    <PolicyForm mode="edit" initial={p} onSave={payload => handleUpdate(p.id, payload)} onCancel={() => setEditId(null)} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Read-only notice for non-admin */}
        {!canManage && (
          <div className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-xs text-slate-400">
            <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
            Read-only view. budget.policy.manage required to create or edit policies.
          </div>
        )}
      </div>
    </div>
  );
}
